/** @jsxImportSource @emotion/react */
import { customToast } from 'utils/toast.util';
import { hoverDropShadow } from 'components/shared/twin.styles';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import React, {
  forwardRef,
  ReactElement,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useAppDispatch } from 'store/store';
import { useSelector } from 'react-redux';
import { fetchWebhookDetail, selectWebhookDetail, selectWebhookDetailLoading } from 'slices/webhooks.slice';
import { RootState } from 'reducers/index';
import { Modal } from 'components/UiControls/modal/modal';
import { ModalOptions } from 'utils/enum';
import { sortWebhookEvents } from 'utils/webhook.util';
import { isEmpty } from 'lodash';
import { ReactComponent as CopyIcon } from 'assets/Icons/copy.svg';
import { ReactComponent as EyeIcon } from 'assets/Icons/VisibilityOn.svg';
import { ReactComponent as EyeSlashIcon } from 'assets/Icons/VisibilityOff.svg';
import { ReactComponent as KeyboardArrowDownIcon } from 'assets/Icons/keyboard_arrow_down.svg';
import { ReactComponent as KeyboardArrowUpIcon } from 'assets/Icons/keyboard_arrow_up.svg';
import Loader from 'components/loader/loader';
import { WebhookService } from 'services/webhooks.service';
import { Info } from 'components/shared/Info';
import { Paginator } from 'components/shared/Paginator';
import { format } from 'date-fns';

type IPromiseParams = {
  resolve: (payload: any) => void;
  reject: (err: any) => void;
};

export type WebhookViewModalRef = {
  show: (webhook: any) => Promise<any>;
};

export const WebhookViewModal = forwardRef(
  ({ }, ref: React.Ref<unknown>): ReactElement => {
    useImperativeHandle(ref, () => ({ show }));

    const [isOpen, setIsOpen] = useState(false);
    const [currentWebhookId, setCurrentWebhookId] = useState<string | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [showSecret, setShowSecret] = useState(false);
    const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

    // Redux
    const dispatch = useAppDispatch();
    const webhook = useSelector((state: RootState) => currentWebhookId ? selectWebhookDetail(currentWebhookId)(state) : null);
    const isLoading = useSelector((state: RootState) => currentWebhookId ? selectWebhookDetailLoading(currentWebhookId)(state) : false);

    const pageSize = 5;

    const promiseInfo = useRef<IPromiseParams>({
      resolve: () => { },
      reject: () => { },
    });

    const show = async (webhookData: any): Promise<any> => {
      setCurrentWebhookId(webhookData.webhookId);
      setIsOpen(true);
      setPageNumber(1);

      // Fetch via Redux (Cached)
      dispatch(fetchWebhookDetail(webhookData.webhookId));

      return new Promise((resolve, reject) => {
        promiseInfo.current = { resolve, reject };
      });
    };

    // Removed local fetchDetails as it's handled by Redux



    const hideModal = () => {
      setIsOpen(false);
    };

    const handleClose = () => {
      hideModal();
      promiseInfo.current?.resolve({ option: ModalOptions.CLOSE });
    };

    const toggleLogExpansion = (logId: string) => {
      setExpandedLogId(prev => prev === logId ? null : logId);
    };

    const tryFormatJson = (str?: string) => {
      if (!str) return 'No payload captured.';
      try {
        return JSON.stringify(JSON.parse(str), null, 2);
      } catch {
        return str;
      }
    };

    const handleCopySecret = async (secret: string) => {
      try {
        await navigator.clipboard.writeText(secret);
        customToast.success('Secret copied to clipboard');
      } catch (err) {
        customToast.error('Failed to copy secret');
      }
    };

    return (
      <div css={[customModalCss]}>
        <Modal
          show={isOpen}
          modalClosed={handleClose}
        >
          <div tw="p-2">
            {!webhook || isLoading ? (
              <div tw="flex justify-center items-center h-64">
                <Loader />
              </div>
            ) : (
              <>
                <div tw="flex items-center space-x-3 mb-6 pr-6">
                  <h2 tw="text-16 font-semibold text-sonnant-purple-3 m-0! truncate" title={webhook.name}>{webhook.name}</h2>
                  <span css={[statusBadgeCss(webhook.state)]} tw="flex-shrink-0">
                    {webhook.state}
                  </span>
                </div>

                <div tw="space-y-6">
                  {/* Basic Info */}
                  <div tw="grid grid-cols-1 gap-4 bg-sonnant-grey-1 p-4 rounded border border-sonnant-grey-light">
                    <div>
                      <label tw="block text-11 font-bold text-sonnant-grey-4 uppercase tracking-wider mb-1">Endpoint URL</label>
                      <div tw="text-14 font-mono text-sonnant-grey-7 truncate" title={webhook.url}>{webhook.url}</div>
                    </div>
                    {webhook.secret && (
                      <div>
                        <label tw="block text-11 font-bold text-sonnant-grey-4 uppercase tracking-wider mb-1">Signing Secret (HMAC)</label>
                        <div tw="flex items-center space-x-2">
                          <div tw="text-14 font-mono text-sonnant-grey-7 bg-white px-2 py-1 rounded border border-sonnant-grey-light flex-1">
                            {showSecret ? webhook.secret : '•'.repeat(30)}
                          </div>

                          <div
                            tw="flex cursor-pointer text-sonnant-grey-5 ml-2"
                            css={[hoverDropShadow(0.1)]}
                            title={showSecret ? "Hide secret" : "Show secret"}
                            onClick={() => setShowSecret(!showSecret)}
                          >
                            {showSecret ? <EyeSlashIcon /> : <EyeIcon />}
                          </div>

                          <div
                            tw="flex cursor-pointer text-sonnant-grey-5 ml-2"
                            css={[hoverDropShadow(0.1)]}
                            title="Copy to clipboard"
                            onClick={() => handleCopySecret(webhook.secret || '')}
                          >
                            <CopyIcon />
                          </div>
                        </div>
                      </div>
                    )}
                    <div tw="grid grid-cols-2 gap-4">
                      <div>
                        <label tw="block text-11 font-bold text-sonnant-grey-4 uppercase tracking-wider mb-1">Subscribed Events</label>
                        <div tw="flex flex-wrap gap-2 mt-1">
                          {sortWebhookEvents(webhook.events || []).map((event: string) => (
                            <span
                              key={event}
                              tw="text-11 bg-white text-sonnant-grey-6 px-2 py-0.5 rounded border border-sonnant-grey-light uppercase font-bold"
                            >
                              {event}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label tw="block text-11 font-bold text-sonnant-grey-4 uppercase tracking-wider mb-1 flex-wrap">Custom Headers</label>
                        {isEmpty(webhook.headers) ? (
                          <div tw="text-13 text-sonnant-grey-5 italic">None</div>
                        ) : (
                          <div tw="space-y-1 mt-1">
                            {Object.entries(webhook.headers || {}).map(([k, v]) => (
                              <div key={k} tw="text-13 text-sonnant-grey-6 truncate" title={`${k}: ${v}`}>
                                <span tw="font-semibold">{k}:</span> {v as string}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Delivery History */}
                  <div>
                    <h3 tw="block text-14 font-medium text-sonnant-grey-7 mb-1">Recent Deliveries (Last 20)</h3>
                    {isEmpty(webhook.logs) ? (
                      <Info text="No delivery history available yet." />
                    ) : (
                      <>
                        <div tw="overflow-hidden border border-sonnant-grey-light rounded">
                          <table tw="w-full text-left border-collapse">
                            <thead>
                              <tr tw="bg-sonnant-grey-2 border-b border-sonnant-grey-light">
                                <th tw="w-8 p-3" />
                                <th tw="p-3 text-11 font-bold text-sonnant-grey-5 uppercase tracking-wider">Timestamp</th>
                                <th tw="p-3 text-11 font-bold text-sonnant-grey-5 uppercase tracking-wider">Event</th>
                                <th tw="p-3 text-11 font-bold text-sonnant-grey-5 uppercase tracking-wider">Status</th>
                                <th tw="p-3 text-11 font-bold text-sonnant-grey-5 uppercase tracking-wider">Latency</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(webhook.logs || []).slice((pageNumber - 1) * pageSize, pageNumber * pageSize).map((log: any) => (
                                <React.Fragment key={log.logId}>
                                  <tr
                                    tw="border-b border-sonnant-grey-light last:border-0 hover:bg-sonnant-grey-1 transition-colors cursor-pointer"
                                    onClick={() => toggleLogExpansion(log.logId)}
                                  >
                                    <td tw="p-3">
                                      {expandedLogId === log.logId ? (
                                        <KeyboardArrowUpIcon tw="w-5 h-5 text-sonnant-grey-5" />
                                      ) : (
                                        <KeyboardArrowDownIcon tw="w-5 h-5 text-sonnant-grey-5" />
                                      )}
                                    </td>
                                    <td tw="p-3 text-13 text-sonnant-grey-6 whitespace-nowrap">
                                      {format(new Date(log.timestamp), 'MMM d, HH:mm:ss')}
                                    </td>
                                    <td tw="p-3 text-13 text-sonnant-grey-6">
                                      <span tw="font-mono text-11 bg-sonnant-grey-2 px-1.5 py-0.5 rounded">
                                        {log.eventType}
                                      </span>
                                    </td>
                                    <td tw="p-3">
                                      <div tw="flex items-center space-x-2">
                                        <span css={[
                                          tw`w-2 h-2 rounded-full`,
                                          log.success ? tw`bg-sonnant-green` : tw`bg-sonnant-red`
                                        ]} />
                                        <span tw="text-13 font-medium text-sonnant-grey-7">
                                          {log.statusCode || 'Error'}
                                        </span>
                                      </div>
                                    </td>
                                    <td tw="p-3 text-13 text-sonnant-grey-5">
                                      {log.latency}ms
                                    </td>
                                  </tr>
                                  {expandedLogId === log.logId && (
                                    <tr tw="bg-sonnant-grey-1 border-b border-sonnant-grey-light">
                                      <td colSpan={5} tw="p-4">
                                        <div tw="text-11 font-bold text-sonnant-grey-5 uppercase tracking-wider mb-2">Payload</div>
                                        <div tw="bg-white border border-sonnant-grey-light rounded p-3 font-mono text-12 text-sonnant-grey-7 whitespace-pre-wrap overflow-x-auto max-h-60">
                                          {tryFormatJson(log.payload)}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div tw="mt-4 flex justify-center" css={css`
                          button {
                            ${tw`text-16!`}
                          }
                        `}>
                          <Paginator
                            totalItemsLength={webhook.logs?.length || 0}
                            pageSize={pageSize}
                            pageNumber={pageNumber}
                            setPageNumber={setPageNumber}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div tw="mt-9 flex justify-end">
                  <button
                    type="button"
                    className="button btn-secondary large"
                    tw="py-2! text-15!"
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      </div>
    );
  },
);

const statusBadgeCss = (state: string) => {
  const colors: Record<string, any> = {
    pending: tw`bg-[var(--color-orange)] text-white`,
    inactive: tw`bg-[var(--color-grey-4)] text-white`,
    active: tw`bg-[var(--color-green)] text-white`,
    failed: tw`bg-[var(--color-red)] text-white`,
  };
  return [
    tw`px-2 py-0.5 rounded text-11 font-bold uppercase tracking-wider`,
    colors[state] || colors.inactive,
  ];
};

const customModalCss = css`
  .Modal {
    ${tw`(width[64rem] left[calc(50% - 32rem)])!`}
  }
`;
